import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  CircularProgress,
  Tab,
  Tabs
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  PlayArrow as TestIcon,
  Delete as DeleteIcon,
  NavigateNext as NavigateNextIcon,
  Schedule as ScheduleIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import automationService from '../../services/automationService';

const WorkflowDetail = () => {
  const { id, workflowId } = useParams();
  const navigate = useNavigate();
  const projectId = id; // The project ID is the 'id' param from the parent route
  
  // Try to get project from outlet context if available
  let contextData = {};
  try {
    contextData = useOutletContext() || {};
  } catch (e) {
    // If not in outlet context, that's okay
  }
  
  const [loading, setLoading] = useState(true);
  const [workflow, setWorkflow] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadWorkflowData();
  }, [workflowId]);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      const [workflowRes, executionsRes, logsRes] = await Promise.all([
        automationService.getWorkflow(workflowId),
        automationService.getExecutions(workflowId, { limit: 10 }),
        automationService.getWorkflowLogs(workflowId, { limit: 50 })
      ]);
      
      setWorkflow(workflowRes.data?.workflow || workflowRes.workflow);
      setExecutions(executionsRes.data?.executions || executionsRes.executions || []);
      setLogs(logsRes.data?.logs || logsRes.logs || []);
    } catch (error) {
      console.error('Error loading workflow:', error);
      setError('Failed to load workflow details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/projects/${projectId}/automation/workflows/${workflowId}/edit`);
  };

  const handleTest = async () => {
    try {
      await automationService.testWorkflow(workflowId, {
        testData: {
          task: {
            _id: 'test-task-id',
            title: 'Test Task',
            status: 'todo'
          }
        }
      });
      await loadWorkflowData();
    } catch (error) {
      console.error('Error testing workflow:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      await automationService.deleteWorkflow(workflowId);
      navigate(`/projects/${projectId}/automation`);
    } catch (error) {
      console.error('Error deleting workflow:', error);
    }
  };

  const handleBack = () => {
    navigate(`/projects/${projectId}/automation`);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <ScheduleIcon color="info" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !workflow) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Alert severity="error">
          {error || 'Workflow not found'}
        </Alert>
        <Button 
          onClick={handleBack} 
          startIcon={<BackIcon />} 
          sx={{ mt: 2 }}
        >
          Back to Automation
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/projects/${projectId}`)}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Project
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/projects/${projectId}/automation`)}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Automation
        </Link>
        <Typography color="text.primary">
          {workflow.name}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {workflow.name}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label={workflow.settings?.enabled ? 'Active' : 'Inactive'}
              color={workflow.settings?.enabled ? 'success' : 'default'}
              size="small"
            />
            <Chip
              label={workflow.trigger?.type.replace(/_/g, ' ')}
              variant="outlined"
              size="small"
            />
            {workflow.category && (
              <Chip
                label={workflow.category}
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          <Button
            startIcon={<EditIcon />}
            onClick={handleEdit}
            variant="outlined"
          >
            Edit
          </Button>
          <Button
            startIcon={<TestIcon />}
            onClick={handleTest}
            variant="contained"
          >
            Test
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            color="error"
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      {/* Main content */}
      <Grid container spacing={3}>
        {/* Workflow details */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Description"
                    secondary={workflow.description || 'No description'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Created"
                    secondary={new Date(workflow.createdAt).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Last Updated"
                    secondary={new Date(workflow.updatedAt).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Version"
                    secondary={workflow.version?.number || workflow.version || '1.0.0'}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Settings
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Priority"
                    secondary={workflow.settings?.priority || 5}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Max Executions"
                    secondary={workflow.settings?.maxExecutions || 'Unlimited'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Allow Chaining"
                    secondary={workflow.chainBehavior?.allowChaining ? 'Yes' : 'No'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Statistics */}
          {workflow.executionStats && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Statistics
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Total Executions"
                      secondary={workflow.executionStats.total || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Success Rate"
                      secondary={`${Math.round(workflow.executionStats.successRate || 0)}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Recent Failures"
                      secondary={workflow.executionStats.recentFailures || 0}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Tabs for executions and logs */}
        <Grid item xs={12} md={8}>
          <Paper>
            <Tabs 
              value={selectedTab} 
              onChange={(e, v) => setSelectedTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={`Executions (${executions.length})`} />
              <Tab label={`Logs (${logs.length})`} />
              <Tab label="Workflow Graph" />
            </Tabs>

            {/* Executions tab */}
            {selectedTab === 0 && (
              <Box sx={{ p: 2 }}>
                {executions.length === 0 ? (
                  <Alert severity="info">No executions yet</Alert>
                ) : (
                  <List>
                    {executions.map((execution) => (
                      <ListItem key={execution._id} disablePadding>
                        <ListItemButton
                          onClick={() => navigate(`/projects/${projectId}/automation/executions/${execution._id}`)}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                {getStatusIcon(execution.status)}
                                <Typography variant="body1">
                                  {new Date(execution.createdAt).toLocaleString()}
                                </Typography>
                              </Stack>
                            }
                            secondary={
                              <Stack direction="row" spacing={1}>
                                <Typography variant="caption">
                                  Trigger: {execution.trigger?.type}
                                </Typography>
                                {execution.duration && (
                                  <Typography variant="caption">
                                    Duration: {execution.duration}ms
                                  </Typography>
                                )}
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Logs tab */}
            {selectedTab === 1 && (
              <Box sx={{ p: 2 }}>
                {logs.length === 0 ? (
                  <Alert severity="info">No logs available</Alert>
                ) : (
                  <List dense>
                    {logs.map((log, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={log.message}
                          secondary={new Date(log.timestamp).toLocaleString()}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Workflow graph tab */}
            {selectedTab === 2 && (
              <Box sx={{ p: 2, minHeight: 400 }}>
                <Alert severity="info">
                  Workflow visualization will be displayed here
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WorkflowDetail;